import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import "./GalleryPage.css";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// Import the project icons
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import NatureIcon from "@mui/icons-material/Nature";
import CakeIcon from "@mui/icons-material/Cake";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import SchoolIcon from "@mui/icons-material/School";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";

// Import all project images
// Baby Products Images
import baby1 from "../images/baby_products/20240708_222204.jpg";
import baby2 from "../images/baby_products/20240709_173022.jpg";
import baby3 from "../images/baby_products/20240709_173038.jpg";
import baby4 from "../images/baby_products/20240709_173607.jpg";
import baby5 from "../images/baby_products/20240709_182307.jpg";
import baby6 from "../images/baby_products/20240709_184040.jpg";

// Bat Mitzvah Images
import batMitzva1 from "../images/bat_mitzva/20250227_210620.jpg";
import batMitzva2 from "../images/bat_mitzva/20250227_214035.jpg";
import batMitzva3 from "../images/bat_mitzva/20250227_220520.jpg";

// Hanukka Gelt Images
import chanukah1 from "../images/hanukka_gelt/20181124_191456.jpg";
import chanukah2 from "../images/hanukka_gelt/20181124_191709.jpg";
import chanukah3 from "../images/hanukka_gelt/20191219_182222.jpg";
import chanukah4 from "../images/hanukka_gelt/20191219_182242.jpg";
import chanukah5 from "../images/hanukka_gelt/20191219_182250.jpg";
import chanukah6 from "../images/hanukka_gelt/20201203_171641.jpg";
import chanukah7 from "../images/hanukka_gelt/20201203_171814.jpg";
import chanukah8 from "../images/hanukka_gelt/20201203_172231.jpg";
import chanukah9 from "../images/hanukka_gelt/20211118_175956.jpg";
import chanukah10 from "../images/hanukka_gelt/20221208_171605.jpg";
import chanukah11 from "../images/hanukka_gelt/20221208_175416.jpg";
import chanukah12 from "../images/hanukka_gelt/20221208_175417.jpg";
import chanukah13 from "../images/hanukka_gelt/20241223_190057.jpg";
import chanukah14 from "../images/hanukka_gelt/20241223_190120.jpg";
import chanukah15 from "../images/hanukka_gelt/20241223_190716.jpg";

// School Supplies Images
import school1 from "../images/school_supplies/20240819_172719.jpg";
import school2 from "../images/school_supplies/20240819_172720.jpg";
import school3 from "../images/school_supplies/20240819_172728.jpg";
import school4 from "../images/school_supplies/20240819_172731.jpg";
import school5 from "../images/school_supplies/20240819_180536.jpg";
import school6 from "../images/school_supplies/20240819_181507.jpg";

// Tu Bishvat Images
import tuBishvat1 from "../images/tu_bishvat/20250208_182352(0).jpg";
import tuBishvat2 from "../images/tu_bishvat/20250208_210917.jpg";
import tuBishvat3 from "../images/tu_bishvat/20250208_215251.jpg";

// Import cleaning supplies images
import cleaning1 from "../images/cleaning_supplies/WhatsApp Image 2025-04-02 at 22.30.45.jpeg";
import cleaning2 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.14 (1).jpeg";
import cleaning3 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.14.jpeg";
import cleaning4 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.15 (1).jpeg";
import cleaning5 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.15 (2).jpeg";
import cleaning6 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.15.jpeg";
import cleaning7 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.16 (1).jpeg";
import cleaning8 from "../images/cleaning_supplies/WhatsApp Image 2025-04-03 at 00.06.16.jpeg";

// New Recruits Kits Images
import recruits1 from "../images/new_recruits_kits/1.png";
import recruits2 from "../images/new_recruits_kits/WhatsApp Image 2025-05-22 at 16.55.46.jpeg";

// Hospital Care Packs Images
import hospital1 from "../images/Hospital_care_packs/1.jpeg";
import hospital2 from "../images/Hospital_care_packs/WhatsApp Image 2026-07-02 at 07.36.32.jpeg";
import hospital3 from "../images/Hospital_care_packs/WhatsApp Image 2026-07-02 at 07.36.53.jpeg";
import hospital4 from "../images/Hospital_care_packs/WhatsApp Image 2026-07-02 at 07.38.37.jpeg";

interface GalleryImage {
  id: number;
  title: string;
  url: string;
  largeUrl: string;
  projectKey: string;
}

const GalleryPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t, language, getLanguageUrl } = useLanguage();
  const navigate = useNavigate();
  const imagesPerPage = 25; // Changed from 24 to 25

  // Get project icon based on project key
  const getProjectIcon = (projectKey: string) => {
    switch (projectKey) {
      case "project.cleaning":
        return <CleaningServicesIcon />;
      case "project.tubshvat":
        return <NatureIcon />;
      case "project.batmitzvah":
        return <CakeIcon />;
      case "project.baby":
        return <BabyChangingStationIcon />;
      case "project.school":
        return <SchoolIcon />;
      case "project.chanukah":
        return <CurrencyExchangeIcon />;
      case "project.hospital":
        return <LocalHospitalIcon />;
      case "project.soldiers":
        return <MilitaryTechIcon />;
      default:
        return null;
    }
  };

  // Memoize the allProjectImages array
  const allProjectImages = useMemo(
    () => [
      // Baby Products
      {
        id: 1,
        url: baby1,
        largeUrl: baby1,
        title: t("project.baby"),
        projectKey: "project.baby",
      },
      {
        id: 2,
        url: baby2,
        largeUrl: baby2,
        title: t("project.baby"),
        projectKey: "project.baby",
      },
      {
        id: 3,
        url: baby3,
        largeUrl: baby3,
        title: t("project.baby"),
        projectKey: "project.baby",
      },
      {
        id: 4,
        url: baby4,
        largeUrl: baby4,
        title: t("project.baby"),
        projectKey: "project.baby",
      },
      {
        id: 5,
        url: baby5,
        largeUrl: baby5,
        title: t("project.baby"),
        projectKey: "project.baby",
      },
      {
        id: 6,
        url: baby6,
        largeUrl: baby6,
        title: t("project.baby"),
        projectKey: "project.baby",
      },

      // Bat Mitzvah
      {
        id: 7,
        url: batMitzva1,
        largeUrl: batMitzva1,
        title: t("project.batmitzvah"),
        projectKey: "project.batmitzvah",
      },
      {
        id: 8,
        url: batMitzva2,
        largeUrl: batMitzva2,
        title: t("project.batmitzvah"),
        projectKey: "project.batmitzvah",
      },
      {
        id: 9,
        url: batMitzva3,
        largeUrl: batMitzva3,
        title: t("project.batmitzvah"),
        projectKey: "project.batmitzvah",
      },

      // Chanukah
      {
        id: 10,
        url: chanukah1,
        largeUrl: chanukah1,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 11,
        url: chanukah2,
        largeUrl: chanukah2,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 12,
        url: chanukah3,
        largeUrl: chanukah3,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 13,
        url: chanukah4,
        largeUrl: chanukah4,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 14,
        url: chanukah5,
        largeUrl: chanukah5,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 15,
        url: chanukah6,
        largeUrl: chanukah6,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 16,
        url: chanukah7,
        largeUrl: chanukah7,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 17,
        url: chanukah8,
        largeUrl: chanukah8,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 18,
        url: chanukah9,
        largeUrl: chanukah9,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 19,
        url: chanukah10,
        largeUrl: chanukah10,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 20,
        url: chanukah11,
        largeUrl: chanukah11,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 21,
        url: chanukah12,
        largeUrl: chanukah12,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 22,
        url: chanukah13,
        largeUrl: chanukah13,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 23,
        url: chanukah14,
        largeUrl: chanukah14,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },
      {
        id: 24,
        url: chanukah15,
        largeUrl: chanukah15,
        title: t("project.chanukah"),
        projectKey: "project.chanukah",
      },

      // School Supplies
      {
        id: 25,
        url: school1,
        largeUrl: school1,
        title: t("project.school"),
        projectKey: "project.school",
      },
      {
        id: 26,
        url: school2,
        largeUrl: school2,
        title: t("project.school"),
        projectKey: "project.school",
      },
      {
        id: 27,
        url: school3,
        largeUrl: school3,
        title: t("project.school"),
        projectKey: "project.school",
      },
      {
        id: 28,
        url: school4,
        largeUrl: school4,
        title: t("project.school"),
        projectKey: "project.school",
      },
      {
        id: 29,
        url: school5,
        largeUrl: school5,
        title: t("project.school"),
        projectKey: "project.school",
      },
      {
        id: 30,
        url: school6,
        largeUrl: school6,
        title: t("project.school"),
        projectKey: "project.school",
      },

      // Tu Bishvat
      {
        id: 31,
        url: tuBishvat1,
        largeUrl: tuBishvat1,
        title: t("project.tubshvat"),
        projectKey: "project.tubshvat",
      },
      {
        id: 32,
        url: tuBishvat2,
        largeUrl: tuBishvat2,
        title: t("project.tubshvat"),
        projectKey: "project.tubshvat",
      },
      {
        id: 33,
        url: tuBishvat3,
        largeUrl: tuBishvat3,
        title: t("project.tubshvat"),
        projectKey: "project.tubshvat",
      },

      // Cleaning Supplies
      {
        id: 34,
        url: cleaning1,
        largeUrl: cleaning1,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 35,
        url: cleaning2,
        largeUrl: cleaning2,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 36,
        url: cleaning3,
        largeUrl: cleaning3,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 37,
        url: cleaning4,
        largeUrl: cleaning4,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 38,
        url: cleaning5,
        largeUrl: cleaning5,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 39,
        url: cleaning6,
        largeUrl: cleaning6,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 40,
        url: cleaning7,
        largeUrl: cleaning7,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },
      {
        id: 41,
        url: cleaning8,
        largeUrl: cleaning8,
        title: t("project.cleaning"),
        projectKey: "project.cleaning",
      },

      // New Recruits Kits
      {
        id: 42,
        url: recruits1,
        largeUrl: recruits1,
        title: t("project.soldiers"),
        projectKey: "project.soldiers",
      },
      {
        id: 43,
        url: recruits2,
        largeUrl: recruits2,
        title: t("project.soldiers"),
        projectKey: "project.soldiers",
      },
      // Hospital Care Packs
      {
        id: 44,
        url: hospital1,
        largeUrl: hospital1,
        title: t("project.hospital"),
        projectKey: "project.hospital",
      },
      {
        id: 45,
        url: hospital2,
        largeUrl: hospital2,
        title: t("project.hospital"),
        projectKey: "project.hospital",
      },
      {
        id: 46,
        url: hospital3,
        largeUrl: hospital3,
        title: t("project.hospital"),
        projectKey: "project.hospital",
      },
      {
        id: 47,
        url: hospital4,
        largeUrl: hospital4,
        title: t("project.hospital"),
        projectKey: "project.hospital",
      },
    ],
    [t],
  ); // Only recreate when translations change

  // Use useMemo to shuffle images only once on component mount
  const shuffledImages = useMemo(() => {
    const shuffleArray = (array: GalleryImage[]) => {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    };

    return shuffleArray(allProjectImages);
  }, [allProjectImages]); // Empty dependency array means this will only run once on mount

  const totalPages = Math.ceil(shuffledImages.length / imagesPerPage);
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = shuffledImages.slice(
    indexOfFirstImage,
    indexOfLastImage,
  );

  const nextPage = () => {
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleImageClick = async (image: GalleryImage) => {
    setSelectedImage(image);
    setIsLoading(true);
    await preloadImage(image.largeUrl);
    setIsLoading(false);
  };

  const preloadImage = (url: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
  };

  const handleNextImage = useCallback(async () => {
    if (!selectedImage) return;

    const currentIndex = shuffledImages.findIndex(
      (img) => img.id === selectedImage.id,
    );
    const nextImage = shuffledImages[currentIndex + 1];

    if (nextImage) {
      setIsLoading(true);
      await preloadImage(nextImage.largeUrl);
      setSelectedImage(nextImage);
      setIsLoading(false);
    }
  }, [selectedImage, shuffledImages]);

  const handlePrevImage = useCallback(async () => {
    if (!selectedImage) return;

    const currentIndex = shuffledImages.findIndex(
      (img) => img.id === selectedImage.id,
    );
    const prevImage = shuffledImages[currentIndex - 1];

    if (prevImage) {
      setIsLoading(true);
      await preloadImage(prevImage.largeUrl);
      setSelectedImage(prevImage);
      setIsLoading(false);
    }
  }, [selectedImage, shuffledImages]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedImage) return;

      if (e.key === "ArrowLeft") {
        if (language === "he") {
          handleNextImage();
        } else {
          handlePrevImage();
        }
      } else if (e.key === "ArrowRight") {
        if (language === "he") {
          handlePrevImage();
        } else {
          handleNextImage();
        }
      } else if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [selectedImage, language, handleNextImage, handlePrevImage]);

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Map project keys to their corresponding slugs
  const projectRoutes: Record<string, string> = {
    "project.baby": "baby",
    "project.batmitzvah": "batmitzvah",
    "project.chanukah": "chanukah",
    "project.school": "school",
    "project.tubshvat": "tubshvat",
    "project.cleaning": "cleaning",
    "project.hospital": "hospital",
    "project.soldiers": "soldiers",
  };

  // Update the navigateToProject function to use language-specific URLs
  const navigateToProject = useCallback(
    (slug: string) => {
      navigate(getLanguageUrl(`/projects/${slug}`));
    },
    [navigate, getLanguageUrl],
  );

  const handleGoToProject = (projectKey: string) => {
    handleCloseModal();
    if (projectRoutes[projectKey]) {
      navigateToProject(projectRoutes[projectKey]);
    }
  };

  return (
    <div className="gallery-page">
      <h1 className="gallery-page-title">{t("gallery.title")}</h1>
      <div className="gallery-grid">
        {currentImages.map((image) => (
          <div
            key={image.id}
            className="gallery-item"
            onClick={() => handleImageClick(image)}
          >
            <img src={image.url} alt={image.title} loading="lazy" />
            <div className="gallery-item-overlay">
              <h3>{t(image.projectKey)}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="gallery-pagination">
        <button
          className="pagination-arrow"
          onClick={prevPage}
          disabled={currentPage === 1}
        >
          {language === "he" ? <ArrowForwardIos /> : <ArrowBackIos />}
        </button>
        <span className="page-indicator">
          {currentPage} / {totalPages}
        </span>
        <button
          className="pagination-arrow"
          onClick={nextPage}
          disabled={currentPage === totalPages}
        >
          {language === "he" ? <ArrowBackIos /> : <ArrowForwardIos />}
        </button>
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t(selectedImage.projectKey)}</h2>
              <div className="modal-actions">
                {/* Project button moved to header with icon */}
                <button
                  className="modal-project-icon-button"
                  onClick={() => handleGoToProject(selectedImage.projectKey)}
                  title={t("view.project")}
                >
                  {getProjectIcon(selectedImage.projectKey)}
                  <span className="tooltip-text">{t("view.project")}</span>
                </button>
                <button className="modal-button" onClick={handleCloseModal}>
                  <CloseIcon />
                </button>
              </div>
            </div>
            <div className="modal-image-container">
              {isLoading ? (
                <div className="loading-container">
                  <CircularProgress style={{ color: "#3F8739" }} />
                </div>
              ) : (
                <img src={selectedImage.largeUrl} alt={selectedImage.title} />
              )}
            </div>

            {/* Navigation controls without project button */}
            {!isLoading && (
              <div className="modal-navigation-controls">
                <button
                  className="modal-nav-button prev"
                  onClick={handlePrevImage}
                  disabled={
                    !selectedImage ||
                    shuffledImages.indexOf(selectedImage) === 0
                  }
                >
                  {language === "he" ? <ArrowForwardIos /> : <ArrowBackIos />}
                </button>

                <button
                  className="modal-nav-button next"
                  onClick={handleNextImage}
                  disabled={
                    !selectedImage ||
                    shuffledImages.indexOf(selectedImage) ===
                      shuffledImages.length - 1
                  }
                >
                  {language === "he" ? <ArrowBackIos /> : <ArrowForwardIos />}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
